import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import AdminWrapper from "../../../components/AdminWrapper";
import { BASE_URL } from "../../../config/config";
import { FollowUpDialog, FollowUpFilterBar, FollowUpStatus } from "../../../components/followUp/FollowUpUI";
import {
  matchesFilter,
  matchesSearch,
  rowSx,
  sortByUrgency,
  useFollowUpOverrides,
} from "../../../components/followUp/followUpUtils";

// Every move-out in one place: who asked, whether the owner agreed, and which
// agreed ones are held back because the tenant still owes money.

const money = (n) => `Rs ${Number(n || 0).toLocaleString("en-US")}`;
const STATUS_COLOR = {
  PENDING: "warning",
  ACCEPTED: "success",
  REJECTED: "error",
  CANCELLED: "default",
  EXPIRED: "default",
};

export default function MoveOuts() {
  const open = useSelector((state) => state.sidebar.open);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const { record, apply } = useFollowUpOverrides();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token") || sessionStorage.getItem("ots_token");
      const { data } = await axios.get(`${BASE_URL}/admin/move-outs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.data || []);
      setSummary(data.summary || null);
      setError("");
    } catch (err) {
      setError(err.response?.data?.message || "Failed to load move-out requests");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Move-outs that are finished or turned down need nobody chased, so they
  // stay in the table but carry no follow-up colour or filter.
  const allRows = rows.map(apply);
  const openRows = allRows.filter((r) => r.needsFollowUp);
  const tracked = (r) => r.needsFollowUp;
  const visibleRows = sortByUrgency(
    allRows.filter((r) => (!tracked(r) ? filter === "all" : matchesFilter(r, filter)) && matchesSearch(r, search))
  );

  const tiles = summary
    ? [
        ["Awaiting a reply", summary.pending],
        ["Agreed, not finished", summary.agreedNotFinished],
        ["Held for unpaid dues", summary.heldForDues],
        ["Completed", summary.completed],
      ]
    : [];

  return (
    <>
      <AdminWrapper />
      <Box className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`} sx={{ p: { xs: 2, md: 4 } }}>
        <Typography variant="h5" fontWeight={800} gutterBottom>
          Move-outs & Dues
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mb: 3 }}>
          {tiles.map(([label, value]) => (
            <Grid item xs={6} md={3} key={label}>
              <Paper elevation={0} sx={{ p: 2, border: "1px solid #e5e7eb", borderRadius: 2 }}>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
                <Typography variant="h5" fontWeight={800}>{value}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {!loading && rows.length > 0 && (
          <FollowUpFilterBar
            rows={openRows}
            search={search}
            onSearch={setSearch}
            filter={filter}
            onFilter={setFilter}
          />
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Alert severity="info">There are no move-out requests yet.</Alert>
        ) : (
          <Paper elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Property</TableCell>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Asked by</TableCell>
                  <TableCell>Move-out date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Unpaid dues</TableCell>
                  <TableCell>Follow-up</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{ cursor: "pointer", ...(r.needsFollowUp ? rowSx(r) : {}) }}
                    onClick={() => setSelected(r)}
                  >
                    <TableCell>{r.flatName}</TableCell>
                    <TableCell>{r.tenantName}</TableCell>
                    <TableCell>{r.landlordName}</TableCell>
                    <TableCell>{r.requestedBy}</TableCell>
                    <TableCell>{r.effectiveDate ? moment(r.effectiveDate).format("DD MMM YYYY") : "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" color={STATUS_COLOR[r.status] || "default"} label={r.status} />
                      {r.status === "ACCEPTED" && r.settlementStatus === "COMPLETED" && (
                        <Chip size="small" sx={{ ml: 0.5 }} label="Vacated" />
                      )}
                    </TableCell>
                    <TableCell>
                      {r.dues && r.dues.total > 0 ? (
                        <Box>
                          <Typography variant="body2" color="error" fontWeight={700}>{money(r.dues.total)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.dues.items.map((d) => `${d.label}${d.cycleKey ? ` (${d.cycleKey})` : ""}`).join(", ")}
                          </Typography>
                        </Box>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      {r.needsFollowUp ? (
                        <FollowUpStatus row={r} onOpen={() => setSelected(r)} />
                      ) : (
                        <Typography variant="caption" color="text.secondary">Nothing to chase</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <FollowUpDialog
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title={selected ? `${selected.tenantName} → ${selected.flatName}` : "Move-out"}
        subtitle="Move-out"
        canLog={Boolean(selected?.needsFollowUp)}
        details={
          selected
            ? [
                { label: "Asked by", value: selected.requestedBy },
                { label: "Status", value: selected.status },
                { label: "Move-out date", value: selected.effectiveDate ? moment(selected.effectiveDate).format("DD MMM YYYY") : "" },
                { label: "Reason", value: selected.reason },
                {
                  label: "Unpaid dues",
                  value:
                    selected.dues && selected.dues.total > 0
                      ? `${money(selected.dues.total)} — ${selected.dues.items
                          .map((d) => `${d.label}${d.cycleKey ? ` (${d.cycleKey})` : ""}`)
                          .join(", ")}`
                      : "None",
                },
              ]
            : []
        }
        contacts={
          selected
            ? [
                { party: "tenant", name: selected.tenantName, phone: selected.tenantPhone, email: selected.tenantEmail },
                { party: "owner", name: selected.landlordName, phone: selected.landlordPhone, email: selected.landlordEmail },
              ]
            : []
        }
        followUp={selected ? apply(selected).followUp : null}
        onSaved={record}
      />
    </>
  );
}
